import { createContext, useEffect, useReducer, useRef, type ReactNode } from 'react';

import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';

import Loader from 'ui-component/Loader';
import axiosServices from 'utils/axios';
import { splitFullName } from 'utils/splitFullName';

import { LOGIN, LOGOUT, UPDATE_USER } from 'contexts/accountActions';
import accountReducer, { type AccountState } from 'contexts/accountReducer';
import { forgetReturnPath } from 'shared/navigation/returnPath';
import { readCustomerFeatures, type CustomerFeatures } from 'contracts/customer';

// firebase initialize
if (!firebase.apps.length) {
  firebase.initializeApp({
    apiKey: import.meta.env.VITE_APP_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_APP_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_APP_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_APP_FIREBASE_APP_ID,
    measurementId: import.meta.env.VITE_APP_FIREBASE_MEASUREMENT_ID
  });
}

// const
const initialState: AccountState = {
  isLoggedIn: false,
  isInitialized: false,
  user: null,
  sessionEnded: false
};

let _isLoggingOut = false;
let _isRegistering = false;
let _clientRegisteredThisSession = false;
export const getIsLoggingOut = (): boolean => _isLoggingOut;
export const EMAIL_LINK_STORAGE_KEY = 'emailForSignIn';

// Carries firstName/lastName from a sign-in/registration action into the
// onIdTokenChanged listener's register-client call below (NEX-1338). sessionStorage,
// not a plain module variable: on mobile, signInWithPopup falls back to a full-page
// redirect flow, which would wipe an in-memory value before onIdTokenChanged fires.
const PENDING_REGISTRATION_NAME_KEY = 'nexvia_pending_registration_name';

interface PendingRegistrationName {
  firstName: string;
  lastName: string;
}

export function savePendingRegistrationName({ firstName, lastName }: { firstName?: string; lastName?: string }): void {
  if (!firstName && !lastName) return;
  try {
    sessionStorage.setItem(PENDING_REGISTRATION_NAME_KEY, JSON.stringify({ firstName: firstName || '', lastName: lastName || '' }));
  } catch (_) {
    /* sessionStorage unavailable — falls back to splitting displayName downstream */
  }
}

function consumePendingRegistrationName(): PendingRegistrationName | null {
  try {
    const raw = sessionStorage.getItem(PENDING_REGISTRATION_NAME_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(PENDING_REGISTRATION_NAME_KEY);
    return JSON.parse(raw) as PendingRegistrationName;
  } catch (_) {
    return null;
  }
}

// The raw OAuth profile blob firebase hands back varies by provider and isn't covered by
// any src/contracts/ schema — minimal shape from evidence (AGENTS.md §1), not invented:
// Google → given_name/family_name. Apple → firstName/lastName (only on the user's
// first-ever sign-in, and not always). Facebook → first_name/last_name. Whichever
// shape is present, we use it instead of guessing from a combined displayName.
interface OAuthProfile {
  given_name?: string;
  firstName?: string;
  first_name?: string;
  family_name?: string;
  lastName?: string;
  last_name?: string;
}

function extractGivenFamilyName(additionalUserInfo: firebase.auth.AdditionalUserInfo | null | undefined): PendingRegistrationName | null {
  const profile = additionalUserInfo?.profile as OAuthProfile | null | undefined;
  if (!profile) return null;
  const firstName = profile.given_name || profile.firstName || profile.first_name;
  if (!firstName) return null;
  const lastName = profile.family_name || profile.lastName || profile.last_name || '';
  return { firstName, lastName };
}

const digitsOf = (value: string | null | undefined): string => (value ?? '').replace(/\D/g, '');

/** What the API holds about the client's phone (client phone verification spec, §4.1/§4.3). */
export interface PhoneVerificationState {
  phone: string | null;
  phoneVerified: boolean;
}

// No src/contracts/ schema exists for this endpoint's response — minimal shape from what
// this file actually reads (AGENTS.md §1).
interface CustomerProfileResponse {
  data?: {
    phone?: string | null;
    // Client phone verification: the API's mark (`clients."phoneVerifiedAt" IS NOT NULL`), sent by
    // GET /customer/me, PATCH /customer/me and POST /customer/phone-verification/sync.
    phoneVerified?: boolean;
    name?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    pilotEnabled?: boolean;
    features?: unknown;
  };
}

export interface FirebaseAuthContextValue extends AccountState {
  firebaseRegister: (email: string, password: string) => Promise<firebase.auth.UserCredential>;
  firebaseEmailPasswordSignIn: (email: string, password: string) => Promise<firebase.auth.UserCredential>;
  firebaseGoogleSignIn: () => Promise<firebase.auth.UserCredential>;
  firebaseFacebookSignIn: () => Promise<firebase.auth.UserCredential>;
  firebaseAppleSignIn: () => Promise<firebase.auth.UserCredential>;
  firebaseEmailLinkSignIn: (email: string) => Promise<void>;
  firebaseCompleteEmailLinkSignIn: () => Promise<firebase.auth.UserCredential>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateProfile: () => void;
  updatePhone: (phone: string) => Promise<void>;
  updateName: (params: { firstName: string; lastName: string }) => Promise<void>;
  sendPhoneVerification: (phoneNumber: string) => Promise<string>;
  verifyPhoneOtp: (verificationId: string, otp: string, phoneNumber: string) => Promise<void>;
  /** Refreshes the token, asks the API to store the number it carries, and keeps the answer. */
  syncPhoneVerification: () => Promise<PhoneVerificationState>;
  /** The number on the Firebase account, if any (QuotesPage's shortcut). */
  getFirebasePhoneNumber: () => string | null;
}

// ==============================|| FIREBASE CONTEXT & PROVIDER ||============================== //

const FirebaseContext = createContext<FirebaseAuthContextValue | null>(null);

export function FirebaseProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(accountReducer, initialState);
  const hasReloadedRef = useRef(false);
  const lastAvatarUrlRef = useRef<string | null>(null);
  const avatarVersionRef = useRef<number | null>(null);
  // The last phone state the sync stored, with a counter. The onIdTokenChanged listener below is
  // subscribed once and cannot read fresh state, so it reads this to learn whether a sync landed
  // while its own GET /customer/me was in flight.
  const phoneSyncRef = useRef<{ seq: number } & PhoneVerificationState>({ seq: 0, phone: null, phoneVerified: false });

  useEffect(() => {
    // On mobile, signInWithPopup falls back to a redirect flow.
    // We MUST call getRedirectResult() and wait for it to resolve BEFORE
    // subscribing to onIdTokenChanged. Otherwise, onIdTokenChanged fires
    // with null (no user) before Firebase has processed the redirect result,
    // causing a login loop on mobile browsers.
    let unsubscribe: firebase.Unsubscribe = () => {};

    firebase
      .auth()
      .getRedirectResult()
      .then((result) => {
        const givenFamilyName = extractGivenFamilyName(result?.additionalUserInfo);
        if (givenFamilyName) savePendingRegistrationName(givenFamilyName);
      })
      .catch((err) => {
        // Only log unexpected errors; cancelled redirects are normal
        if (err?.code && err.code !== 'auth/cancelled-popup-request') {
          console.error('[Firebase] getRedirectResult error:', err.code, err.message);
        }
      })
      .finally(() => {
        // Now that any pending redirect is resolved, subscribe to auth state.
        // At this point Firebase's internal user state is settled.
        unsubscribe = firebase.auth().onIdTokenChanged(async (user) => {
          if (user) {
            if (!hasReloadedRef.current && !user.photoURL) {
              await user.reload();
              hasReloadedRef.current = true;
            }
            const freshUser = firebase.auth().currentUser || user;
            // Fixed (docs/PENDING-FINDINGS.md finding #102): each providerData entry is typed
            // `UserInfo | null` — the `!` assertion would throw on .photoURL if Firebase ever
            // reported a null entry at index 0.
            const avatarUrl =
              freshUser.photoURL ||
              (freshUser.providerData && freshUser.providerData.length > 0 ? (freshUser.providerData[0]?.photoURL ?? null) : null);

            if (avatarUrl && avatarUrl !== lastAvatarUrlRef.current) {
              avatarVersionRef.current = Date.now();
              lastAvatarUrlRef.current = avatarUrl;
            }

            const token = await freshUser.getIdToken();
            localStorage.setItem('serviceToken', token);

            // Fetch phone, name and pilotEnabled from Nexvia DB (fire-and-forget, fallback to null/false)
            let nexviaPhone: string | null = null;
            let phoneVerified = false;
            let nexviaName: string | null = null;
            let nexviaFirstName: string | null = null;
            let nexviaLastName: string | null = null;
            let pilotEnabled = false;
            let features: CustomerFeatures = { passengerTransport: false };
            // A forced token refresh fires this listener while a sync is in flight; so does
            // updatePhoneNumber. A GET that started before the sync was stored can answer with
            // the old phone state and land after it. The sync's answer wins then.
            const phoneSyncSeq = phoneSyncRef.current.seq;
            try {
              const profileRes = await axiosServices.get<CustomerProfileResponse>('/customer/me');
              nexviaPhone = profileRes.data?.data?.phone ?? null;
              // Only the API's mark counts. A phone alone is not a verified phone.
              phoneVerified = profileRes.data?.data?.phoneVerified === true;
              nexviaName = profileRes.data?.data?.name ?? null;
              nexviaFirstName = profileRes.data?.data?.firstName ?? null;
              nexviaLastName = profileRes.data?.data?.lastName ?? null;
              pilotEnabled = profileRes.data?.data?.pilotEnabled ?? false;
              features = readCustomerFeatures(profileRes.data?.data);
            } catch (_) {
              /* user may not be registered yet */
            }
            if (phoneSyncRef.current.seq !== phoneSyncSeq) {
              nexviaPhone = phoneSyncRef.current.phone;
              phoneVerified = phoneSyncRef.current.phoneVerified;
            }

            dispatch({
              type: LOGIN,
              payload: {
                isLoggedIn: true,
                user: {
                  id: freshUser.uid,
                  email: freshUser.email,
                  avatar: avatarUrl,
                  // Fixed (docs/PENDING-FINDINGS.md finding #102): freshUser.email can be
                  // null/undefined (e.g. a provider that doesn't share email, like Apple sign-in
                  // without a shared address) — the `!` assertion would throw on .split('@').
                  name: nexviaName || freshUser.displayName || (freshUser.email ? freshUser.email.split('@')[0] : 'Usuario'),
                  firstName: nexviaFirstName,
                  lastName: nexviaLastName,
                  avatarVersion: avatarVersionRef.current,
                  phone: nexviaPhone,
                  phoneVerified,
                  pilotEnabled,
                  features
                }
              }
            });

            if (!_clientRegisteredThisSession && !_isRegistering) {
              _isRegistering = true;
              try {
                const pendingName = consumePendingRegistrationName();
                // Same fix as above (finding #102) — user.email can be null/undefined.
                const { firstName, lastName } =
                  pendingName || splitFullName(user.displayName || (user.email ? user.email.split('@')[0] : 'Usuario'));
                await axiosServices.post<unknown>('/auth/register-client', {
                  phone: '',
                  address: '',
                  firstName,
                  lastName
                });
                _clientRegisteredThisSession = true;
              } catch (error) {
                const err = error as { message?: string; response?: { data?: { message?: string } } };
                const errorMessage = err.message || err.response?.data?.message || '';
                if (errorMessage.includes('duplicate key')) {
                  _clientRegisteredThisSession = true;
                } else {
                  console.error('Error registering user in backend:', error);
                }
              } finally {
                _isRegistering = false;
              }
            }
          } else {
            localStorage.removeItem('serviceToken');
            hasReloadedRef.current = false;
            lastAvatarUrlRef.current = null;
            avatarVersionRef.current = null;
            // The next account must not inherit what a sync stored for this one.
            phoneSyncRef.current = { seq: 0, phone: null, phoneVerified: false };
            dispatch({ type: LOGOUT });
          }
        });
      });

    return () => unsubscribe();
  }, []);

  const firebaseEmailPasswordSignIn = (email: string, password: string) => firebase.auth().signInWithEmailAndPassword(email, password);

  const firebaseGoogleSignIn = async () => {
    const provider = new firebase.auth.GoogleAuthProvider();

    const result = await firebase.auth().signInWithPopup(provider);
    const givenFamilyName = extractGivenFamilyName(result.additionalUserInfo);
    if (givenFamilyName) savePendingRegistrationName(givenFamilyName);
    if (result.user) {
      await result.user.reload();
    }
    return result;
  };

  const firebaseFacebookSignIn = async () => {
    const provider = new firebase.auth.FacebookAuthProvider();
    const result = await firebase.auth().signInWithPopup(provider);
    const givenFamilyName = extractGivenFamilyName(result.additionalUserInfo);
    if (givenFamilyName) savePendingRegistrationName(givenFamilyName);
    return result;
  };

  const firebaseAppleSignIn = async () => {
    const provider = new firebase.auth.OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    const result = await firebase.auth().signInWithPopup(provider);
    const givenFamilyName = extractGivenFamilyName(result.additionalUserInfo);
    if (givenFamilyName) savePendingRegistrationName(givenFamilyName);
    if (result.user) {
      await result.user.reload();
    }
    return result;
  };

  const firebaseEmailLinkSignIn = async (email: string) => {
    const normalizedEmail = email.trim().toLowerCase();
    const actionCodeSettings = {
      url: `${window.location.origin}/email-link-signin`,
      handleCodeInApp: true
    };
    await firebase.auth().sendSignInLinkToEmail(normalizedEmail, actionCodeSettings);
    localStorage.setItem(EMAIL_LINK_STORAGE_KEY, normalizedEmail);
  };

  const firebaseCompleteEmailLinkSignIn = async () => {
    if (!firebase.auth().isSignInWithEmailLink(window.location.href)) {
      throw new Error('INVALID_EMAIL_LINK');
    }
    const email = localStorage.getItem(EMAIL_LINK_STORAGE_KEY);
    if (!email) {
      throw new Error('EMAIL_NOT_FOUND_IN_STORAGE');
    }
    const result = await firebase.auth().signInWithEmailLink(email, window.location.href);
    localStorage.removeItem(EMAIL_LINK_STORAGE_KEY);
    return result;
  };

  const firebaseRegister = async (email: string, password: string) => firebase.auth().createUserWithEmailAndPassword(email, password);

  const logout = () => {
    _isLoggingOut = true;
    _clientRegisteredThisSession = false;
    // NEX-1585: a page remembered for the next sign-in is not for whoever signs in after this one.
    forgetReturnPath();
    sessionStorage.removeItem(PENDING_REGISTRATION_NAME_KEY);
    sessionStorage.setItem('nexvia_logged_out', '1');
    localStorage.removeItem('serviceToken');
    return firebase
      .auth()
      .signOut()
      .finally(() => {
        setTimeout(() => {
          _isLoggingOut = false;
        }, 2000);
      });
  };

  const resetPassword = async (email: string) => {
    await firebase.auth().sendPasswordResetEmail(email);
  };

  const updateProfile = () => {};

  const updatePhone = async (phone: string) => {
    await axiosServices.patch<unknown>('/customer/me', { phone });
    dispatch({
      type: LOGIN,
      payload: {
        isLoggedIn: true,
        user: {
          ...(state.user as NonNullable<AccountState['user']>),
          phone: phone.trim() || null
        }
      }
    });
  };

  const updateName = async ({ firstName, lastName }: { firstName: string; lastName: string }) => {
    await axiosServices.patch<unknown>('/customer/me', { firstName, lastName });
    dispatch({
      type: LOGIN,
      payload: {
        isLoggedIn: true,
        user: {
          ...(state.user as NonNullable<AccountState['user']>),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          name: `${firstName} ${lastName}`.trim()
        }
      }
    });
  };

  const sendPhoneVerification = async (phoneNumber: string) => {
    // Destroy previous verifier
    if (window._nexviaRecaptcha) {
      try {
        window._nexviaRecaptcha.clear();
      } catch (_) {
        /* ignore */
      }
      window._nexviaRecaptcha = null;
    }

    // Create container outside React tree so re-renders can't destroy it
    const existing = document.getElementById('nexvia-recaptcha-root');
    if (existing) existing.remove();
    const container = document.createElement('div');
    container.id = 'nexvia-recaptcha-root';
    document.body.appendChild(container);

    window._nexviaRecaptcha = new firebase.auth.RecaptchaVerifier('nexvia-recaptcha-root', {
      size: 'invisible'
    });
    await window._nexviaRecaptcha.render();

    const provider = new firebase.auth.PhoneAuthProvider();
    const verificationId = await provider.verifyPhoneNumber(phoneNumber, window._nexviaRecaptcha);
    return verificationId;
  };

  const storePhoneVerification = (profile: PhoneVerificationState) => {
    phoneSyncRef.current = { seq: phoneSyncRef.current.seq + 1, ...profile };
    dispatch({ type: UPDATE_USER, payload: { phone: profile.phone, phoneVerified: profile.phoneVerified } });
  };

  const getFirebasePhoneNumber = () => firebase.auth().currentUser?.phoneNumber ?? null;

  const syncPhoneVerification = async (): Promise<PhoneVerificationState> => {
    const currentUser = firebase.auth().currentUser;
    if (!currentUser) {
      throw new Error('No hay una sesión activa para verificar el teléfono. Iniciá sesión de nuevo e intentá otra vez.');
    }
    // The request interceptor (utils/axios.ts:21) sends the cached token, which can predate the
    // number being attached to this account. Only a refreshed token carries phone_number, and the
    // API writes the mark from that claim, never from a request body.
    const uid = currentUser.uid;
    await currentUser.getIdToken(true);
    const res = await axiosServices.post<CustomerProfileResponse>('/customer/phone-verification/sync');
    const profile: PhoneVerificationState = {
      phone: res.data?.data?.phone ?? null,
      phoneVerified: res.data?.data?.phoneVerified === true
    };
    // The answer belongs to the account that asked. If the session switched to another account
    // while the request was in flight, storing it would show this phone on the wrong user.
    if (firebase.auth().currentUser?.uid === uid) {
      storePhoneVerification(profile);
    }
    return profile;
  };

  const verifyPhoneOtp = async (verificationId: string, otp: string, phoneNumber: string) => {
    const credential = firebase.auth.PhoneAuthProvider.credential(verificationId, otp);
    // Fixed (docs/PENDING-FINDINGS.md finding #102): currentUser can genuinely be null here
    // (e.g. the session ended between starting and completing phone verification) — the `!`
    // assertion would throw an unhelpful TypeError instead of a clear, catchable error.
    const currentUser = firebase.auth().currentUser;
    if (!currentUser) {
      throw new Error('No hay una sesión activa para verificar el teléfono. Iniciá sesión de nuevo e intentá otra vez.');
    }
    // Firebase consumes the SMS code on the first updatePhoneNumber. If that call worked but the
    // sync after it failed, the client retries with the same code, and updatePhoneNumber would fail
    // on a code already used. The number is already proven on this account then: only re-sync.
    const alreadyOnAccount = digitsOf(currentUser.phoneNumber) !== '' && digitsOf(currentUser.phoneNumber) === digitsOf(phoneNumber);
    if (!alreadyOnAccount) {
      try {
        await currentUser.updatePhoneNumber(credential);
      } catch (err) {
        // A number already attached to another Firebase account is refused (client phone
        // verification, D5). Firebase did check the code, but it did not attach the number to THIS
        // account, so this account's token can never carry it and the API has no way to verify it.
        // This used to be accepted on purpose and saved with a PATCH that needed no code.
        const code = (err as { code?: string }).code;
        if (code === 'auth/account-exists-with-different-credential' || code === 'auth/credential-already-in-use') {
          throw Object.assign(new Error('PHONE_IN_OTHER_ACCOUNT'), { code: 'PHONE_IN_OTHER_ACCOUNT' });
        }
        throw err;
      }
    }
    // The code is spent from here on. Any failure to confirm the number with the API (network, a
    // missing clients row, the mark not written) is the same dead end for the client: tell them to
    // try again, and a retry only re-syncs (above).
    const notConfirmed = () => Object.assign(new Error('PHONE_NOT_CONFIRMED'), { code: 'PHONE_NOT_CONFIRMED' });
    let profile: PhoneVerificationState;
    try {
      profile = await syncPhoneVerification();
    } catch {
      throw notConfirmed();
    }
    // The API marks the number from the refreshed token, which carries what Firebase stored (not
    // the typed string: Firebase normalises it, e.g. a trunk prefix). Anything but that number,
    // marked, means the mark was not written, and the caller must not tell the client that the
    // phone is verified.
    if (!profile.phoneVerified || digitsOf(profile.phone) !== digitsOf(firebase.auth().currentUser?.phoneNumber)) {
      throw notConfirmed();
    }
  };
  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  return (
    <FirebaseContext
      value={{
        ...state,
        firebaseRegister,
        firebaseEmailPasswordSignIn,
        firebaseGoogleSignIn,
        firebaseFacebookSignIn,
        firebaseAppleSignIn,
        firebaseEmailLinkSignIn,
        firebaseCompleteEmailLinkSignIn,
        logout,
        resetPassword,
        updateProfile,
        updatePhone,
        updateName,
        sendPhoneVerification,
        verifyPhoneOtp,
        syncPhoneVerification,
        getFirebasePhoneNumber
      }}
    >
      {children}
    </FirebaseContext>
  );
}

export default FirebaseContext;
